/**
 * Accounts a caller can actually choose between (ACOP-79).
 *
 * A single-account user must NEVER be shown the account picker, so this is deliberately strict
 * rather than trusting the session payload's shape:
 *   - anything that is not an array yields no choices
 *   - entries without a usable string id are dropped
 *   - ids are de-duplicated, because two portal engagements pointing at the SAME account are one
 *     choice, not two — without this such a user would be offered a picker listing their own
 *     account twice
 *
 * Salesforce de-duplicates as well. Doing it on both sides means a regression on either one cannot
 * put a picker in front of someone who only belongs to one account.
 *
 * Pure and dependency-free so it can be unit-tested without rendering React.
 */
export function selectableAccounts(accountCtx: any): { id: string; name?: string }[] {
  const raw = accountCtx?.accounts;
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: { id: string; name?: string }[] = [];
  for (const a of raw) {
    if (!a || typeof a.id !== "string" || !a.id) continue;
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    out.push(a);
  }
  return out;
}

/** True only when the caller genuinely has more than one account to switch between. */
export function shouldShowAccountPicker(accountCtx: any): boolean {
  return selectableAccounts(accountCtx).length > 1;
}
